import React, { useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@utils/cn';

export interface ExpandableTextProps {
  /** The text to display. */
  text: string;
  /** Number of lines to clamp to when collapsed. Defaults to 3. */
  clampLines?: number;
  className?: string;
}

/**
 * Displays a block of text clamped to `clampLines`, with an inline
 * "Show more" / "Show less" toggle that only appears when the text actually
 * overflows the clamped height. No fixed pixel height, no inner scroll — safe
 * to place inside an already-scrolling container.
 */
export const ExpandableText: React.FC<ExpandableTextProps> = ({
  text,
  clampLines = 3,
  className,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  // Detect overflow by comparing the scroll height to the clamped client height.
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;
    // Only meaningful while collapsed; when expanded the clamp is removed.
    if (!expanded) {
      setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
    }
  }, [text, clampLines, expanded]);

  const clampClass =
    clampLines === 1
      ? 'line-clamp-1'
      : clampLines === 2
        ? 'line-clamp-2'
        : 'line-clamp-3';

  return (
    <div className={className}>
      <p
        ref={textRef}
        className={cn(
          'text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line break-words',
          !expanded && clampClass,
        )}>
        {text}
      </p>
      {(isOverflowing || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className={cn(
            'mt-1 text-sm text-indigo-600 dark:text-indigo-400',
            'hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors',
          )}>
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
};

export default ExpandableText;
